import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  subscribeReleaseFlagDeferral,
  type DoneDeferral,
} from '../../services/releaseFlagDeferral';
import { useRobotEnabled } from '../../hooks/useRobotEnabled'; // MKS-338

/**
 * Phase 120 (Plan 120-04, D-120-23) — global notice for a deferred Done move.
 *
 * "Done" means "live for users": a flagged ticket whose release flag is still
 * off in production is not marked Done by a state change (the API answers 200
 * with `deferred`, and the card snaps back on refetch). This in-flow row tells
 * the person who clicked why, and where an admin switches the flag on. The
 * latest deferral replaces any earlier one; Dismiss hides it.
 */
export function ReleaseFlagDeferralNotice() {
  const robotEnabled = useRobotEnabled();
  const [deferral, setDeferral] = useState<DoneDeferral | null>(null);

  useEffect(() => subscribeReleaseFlagDeferral(setDeferral), []);

  // MKS-338 — "Done means live for users" is the robot / Factory™ QA process,
  // and this notice points at an admin page that belongs to it. With the
  // add-on off the backend never defers a Done move at all, so this is the
  // presentation half of the same gate. The subscription stays mounted either
  // way — the hooks above must not become conditional.
  if (!robotEnabled) return null;
  if (!deferral) return null;

  if (deferral.reason === 'factory_family_incomplete') {
    const pending = deferral.pendingRepositories ?? [];
    return (
      <div
        role="status"
        className="shrink-0 bg-amber-500 text-white px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-sm font-medium shadow-md"
      >
        <span>
          Not moved to Done yet: waiting for the rest of the Factory™ change
          {pending.length > 0 ? ` (${pending.join(', ')})` : ''}.
        </span>
        <button
          type="button"
          onClick={() => setDeferral(null)}
          className="rounded bg-amber-700 hover:bg-amber-800 px-3 py-1 text-xs font-medium transition-colors"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    // In-flow layout row, same placement style as ImpersonationBanner.
    <div
      role="status"
      className="shrink-0 bg-amber-500 text-white px-4 py-2 flex flex-wrap items-center justify-center gap-3 text-sm font-medium shadow-md"
    >
      <span>
        <strong>{deferral.ticket}</strong> was not marked Done: its release flag{' '}
        {deferral.flagKeys.map((key, index) => (
          <span key={key}>
            {index > 0 ? ', ' : ''}
            <code className="rounded bg-amber-700 px-1 py-0.5 text-xs">{key}</code>
          </span>
        ))}{' '}
        is off in production until verified. It will close automatically once the flag is switched
        on.
      </span>
      {typeof deferral.projectId === 'number' ? (
        <Link
          to={`/projects/${deferral.projectId}/edit?tab=engineering`}
          className="rounded bg-amber-700 hover:bg-amber-800 px-3 py-1 text-xs font-medium transition-colors"
        >
          Open Engineering
        </Link>
      ) : (
        <span>Switch it on from the project&apos;s Engineering tab.</span>
      )}
      <button
        type="button"
        onClick={() => setDeferral(null)}
        className="rounded bg-amber-700 hover:bg-amber-800 px-3 py-1 text-xs font-medium transition-colors"
      >
        Dismiss
      </button>
    </div>
  );
}
